/**
 * Stale-bundle detection (operator UX safeguard).
 *
 * A deploy ships new, content-hashed JS, but a tab opened before the deploy keeps running the old
 * bundle until it reloads — and because `index.html` revalidates while hashed assets cache forever, a
 * soft reload does not always pull the new app. This module lets the running SPA notice that a newer
 * build is deployed and offer the operator a reload, so a stale tab can never silently keep running
 * code a fix has already replaced (the "Invalid time value" crash was prolonged exactly this way).
 *
 * {@link APP_VERSION} is the build id baked into THIS bundle (Vite `define`). {@link fetchDeployedVersion}
 * reads the CURRENTLY deployed id from the BFF's `GET /version` (served `no-store`, so it is never a
 * cached answer). A difference means a newer build is live. Nothing here reloads on its own — the
 * caller decides — and every failure is swallowed to a `null`, so a transient network blip never
 * nags the operator.
 */

/** The build id compiled into this bundle. Replaced at build time by Vite's `define`. */
export const APP_VERSION: string =
  typeof __APP_VERSION__ === "string" ? __APP_VERSION__ : "dev";

/** How often to check for a newer deployed build. Five minutes is frequent enough, cheap enough. */
export const VERSION_POLL_MS = 5 * 60_000;

/**
 * The version the BFF is currently serving, or `null` when it cannot be read (offline, a transient
 * error, or an older BFF without the `/version` route). Never throws.
 */
export async function fetchDeployedVersion(signal?: AbortSignal): Promise<string | null> {
  try {
    const init: RequestInit = {
      // Never a cached answer: we are asking "what is live right now".
      cache: "no-store",
      headers: { accept: "application/json" },
    };
    // Only set `signal` when present: under exactOptionalPropertyTypes an `undefined` signal is not
    // assignable to `AbortSignal | null`.
    if (signal !== undefined) init.signal = signal;
    const res = await fetch("/version", init);
    if (!res.ok) return null;
    const body = (await res.json()) as { version?: unknown };
    return typeof body.version === "string" && body.version.length > 0 ? body.version : null;
  } catch {
    return null;
  }
}

/**
 * True when a newer build is deployed than the one running. A `null` deployed version (unreadable)
 * is treated as "no update" so a blip never prompts a reload; `"dev"` (an un-defined local build)
 * never reports an update.
 */
export function isNewerDeployed(running: string, deployed: string | null): boolean {
  if (deployed === null || running === "dev") return false;
  return deployed !== running;
}
